import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../providers/AppStore";
import { updateLogicStatus } from "../../../../common/network/Api";
import IOSSwitch from "../common/IOSSwitch";
import { useSelector } from "react-redux";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import useTheme from "../../../../common/hooks/useTheme";
import type { ServiceDetails } from "./types";
import { useNavigate } from "react-router-dom";

interface RelevantLogicsSectionProps {
  service: ServiceDetails;
  loadData: () => Promise<void>;
}

const RelevantLogicsSection: React.FC<RelevantLogicsSectionProps> = ({
  service,
  loadData,
}) => {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();

  const savedLogics = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLogics
  );

  // Filter logics that include this service
  const relevantLogics = React.useMemo(() => {
    if (!savedLogics || savedLogics.length === 0) {
      return [];
    }

    // Match by userServiceID
    return savedLogics.filter((logic: any) => {
      return (
        logic.services &&
        logic.services.some((logicService: any) => {
          return logicService.userServiceID === service.id;
        })
      );
    });
  }, [savedLogics, service.id]);

  const handleToggleLogic = async (logicId: string, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await updateLogicStatus(logicId, newStatus);

      // Refresh data to update the logics
      await loadData();

      // Show success snackbar
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: `Logic ${
          newStatus === 1 ? "enabled" : "disabled"
        } successfully!`,
      });
      ASSetTypeSnackBar("success");
    } catch (error) {
      console.error("Error toggling logic status:", error);
      // Show error snackbar
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update logic status. Please try again.",
      });
      ASSetTypeSnackBar("alert");
    }
  };

  const handleViewLogic = (logicId: string) => {
    navigate(`/client/services/logic/${logicId}`);
  };

  return (
    <div style={styles.relevantLogicsSection(theme)}>
      <div style={styles.relevantLogicsTitle(theme)}>Relevant logic(s)</div>
      {relevantLogics.length > 0 ? (
        <>
          {relevantLogics.map((logic, index) => (
            <div key={logic.id || index} style={styles.logicItem}>
              {/* Logic name box */}
              <div
                style={styles.logicNameBox(theme)}
                onClick={() => handleViewLogic(logic.id)}
              >
                <div style={styles.logicNameContent}>
                  <div style={styles.logicNameColumn}>
                    <div style={styles.logicNameLabel(theme)}>
                      Logic {index + 1}
                    </div>
                    <div style={styles.logicName(theme)}>{logic.name}</div>
                  </div>
                  <div
                    style={styles.logicEnabledContainer}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span style={styles.logicEnabledLabel(theme)}>
                      {logic.enabled === 1 ? "Enabled" : "Disabled"}
                    </span>
                    <IOSSwitch
                      checked={logic.enabled === 1}
                      onChange={() =>
                        handleToggleLogic(logic.id, logic.enabled)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div style={styles.noLogicsMessage(theme)}>
          No logic rules configured for this service
        </div>
      )}
    </div>
  );
};

const styles = {
  relevantLogicsSection: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  }),
  relevantLogicsTitle: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "28px",
  }),
  logicItem: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  logicEnabledContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    cursor: "default",
  },
  logicEnabledLabel: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "16px",
  }),
  logicNameBox: (theme: any) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "0 16px",
    height: "56px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  }),
  logicNameContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  logicNameColumn: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    justifyContent: "center",
  },
  logicNameLabel: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "16px",
  }),
  logicName: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "24px",
  }),
  noLogicsMessage: (theme: any) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "22px",
    padding: "8px 0",
  }),
};

export default RelevantLogicsSection;
